/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/protocol-value
 * @desc Grid SmartTable Column Definitions
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { EntityLinkComponent } from 'labstep-web/components/Entity/Link/Component';
import { ICONS } from 'labstep-web/constants/icons';
import ProtocolChildRouter from 'labstep-web/containers/Router/ProtocolChild';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { ProtocolValue } from 'labstep-web/models/protocol-value.model';

export const getProtocolValueNameColumnDef = (
  experimentOrProtocol: Experiment | Protocol,
): SmartTableColDef => ({
  colId: `name`,
  sortable: true,
  headerName: 'Name',
  headerComponent: GridSmartTableColumnHeader,
  headerComponentParams: (params: HeaderComponentParamsProps) => {
    return {
      toolPanelColumnName: `Name`,
      icon: ICONS.protocol_value.primary,
      experimentOrProtocol,
    };
  },
  editable: (params: EditableCallbackParams) =>
    GridSmartTableProtocolValueService.getEditable(params),
  valueGetter: (params: ValueGetterParams) => {
    if (!params.node) {
      return '';
    }

    const protocolValue = params.data as ProtocolValue;

    if (!protocolValue?.name) {
      return '';
    }

    return protocolValue.name;
  },
  cellRenderer: (params: CellRendererParams<ProtocolValue>) => {
    const protocolValue = params.data as ProtocolValue;

    if (!protocolValue?.name) {
      return (
        <span>
          <DataGridPlaceholder
            children={'Enter name'}
            params={params}
            editable={GridSmartTableProtocolValueService.getEditable(
              params,
            )}
          />
        </span>
      );
    }

    return (
      <ProtocolChildRouter
        route={
          protocolValue.is_input ? 'value_show' : 'value_show_output'
        }
        id={protocolValue.guid}
      >
        {({ navigate }) => (
          <EntityLinkComponent
            entity={protocolValue}
            onClick={navigate}
          />
        )}
      </ProtocolChildRouter>
    );
  },
  valueSetter: (params) => {
    const newValue = params.newValue;
    if (
      !params.node ||
      newValue === '' ||
      params.newValue === params.oldValue
    ) {
      return false;
    }

    const protocolValue = params.data as ProtocolValue;

    if (protocolValue) {
      return GridSmartTableProtocolValueService.updateEntity(
        params,
        protocolValue.entityName,
        protocolValue.idAttr,
        {
          name: newValue,
        },
      );
    } else {
      return GridSmartTableProtocolValueService.createEntity(
        params,
        ProtocolValue.entityName,
        {
          name: newValue,
          protocol_condition_id: params.data.guid,
          [`${experimentOrProtocol.entityName}_id`]:
            experimentOrProtocol.guid,
        },
      );
    }
  },
  valueClearer: () => {
    return null;
  },
  suppressKeyboardEvent: suppressKeyboardEventClearCells,
});
